import React from "react";
import {
  Repeat,
  Clock,
  Dumbbell,
  Zap,
  Wind,
  HeartPulse,
  Flame,
  StretchVertical,
  BrainCircuit,
  Activity,
  ArrowRight,
  User,
  Bike,
  Boxes,
} from "lucide-react";
import {
  isTransitionLabel,
  categoriseExercise,
} from "../../utils/planVisuals";

// Per-type icon. Used by getVisualsForType() below.
export const iconMap = {
  Warmup: <Flame />,
  Cooldown: <Wind />,
  Work: <Dumbbell />,
  Rest: <Clock />,
  SetRest: <Clock />,
  Transition: <ArrowRight />,
  Group: <Repeat />,
  Strength: <Dumbbell />,
  Cardio: <HeartPulse />,
  HIIT: <Zap />,
  Circuit: <BrainCircuit />,
  Mobility: <StretchVertical />,
  Default: <Activity />,
};

// Per-exercise category icons — first pass before the type-based lookup so a
// rower / pull-up / ball slam each get a distinct glyph instead of the
// default dumbbell. Driven by categoriseExercise(label) (keyword-based,
// no canonical-key dependency).
export const categoryIconMap = {
  barbell: <Dumbbell />,
  bodyweight: <User />,
  cardio_machine: <Bike />,
  power: <Zap />,
  core: <Boxes />,
};

// Per-type theme. `hover` is the row-hover bg tint — keep these as literal
// Tailwind class strings so JIT picks them up at build time. A per-type
// tint, not the shared neutral `hover:bg-accent`, so a row keeps its colour.
export const themeMap = {
  Warmup: {
    border: "border-l-warmup",
    text: "text-warmup",
    bg: "bg-warmup",
    hover: "hover:bg-warmup/10",
  },
  Cooldown: {
    border: "border-l-cooldown",
    text: "text-cooldown",
    bg: "bg-cooldown",
    hover: "hover:bg-cooldown/10",
  },
  Work: {
    border: "border-l-work",
    text: "text-work",
    bg: "bg-work",
    hover: "hover:bg-work/10",
  },
  Rest: {
    border: "border-l-rest",
    text: "text-rest",
    bg: "bg-rest",
    hover: "hover:bg-rest/10",
  },
  SetRest: {
    border: "border-l-rest",
    text: "text-rest",
    bg: "bg-rest",
    hover: "hover:bg-rest/10",
  },
  Transition: {
    border: "border-l-transition",
    text: "text-transition",
    bg: "bg-transition",
    hover: "hover:bg-transition/10",
  },
  Group: {
    border: "border-l-group",
    text: "text-group",
    bg: "bg-group",
    hover: "hover:bg-group/10",
  },
  Strength: {
    border: "border-l-strength",
    text: "text-strength",
    bg: "bg-strength",
    hover: "hover:bg-strength/10",
  },
  Cardio: {
    border: "border-l-cardio",
    text: "text-cardio",
    bg: "bg-cardio",
    hover: "hover:bg-cardio/10",
  },
  HIIT: {
    border: "border-l-hiit",
    text: "text-hiit",
    bg: "bg-hiit",
    hover: "hover:bg-hiit/10",
  },
  Mobility: {
    border: "border-l-mobility",
    text: "text-mobility",
    bg: "bg-mobility",
    hover: "hover:bg-mobility/10",
  },
  Circuit: {
    border: "border-l-group",
    text: "text-group",
    bg: "bg-group",
    hover: "hover:bg-group/10",
  },
  Default: {
    border: "border-l-default",
    text: "text-default",
    bg: "bg-default",
    hover: "hover:bg-default/10",
  },
};

export const getVisualsForType = (type, label = "") => {
  const safeType = type ? String(type).trim().toLowerCase() : "";
  const safeLabel = label ? String(label).trim().toLowerCase() : "";

  // 0th pass: explicit Transition type, or Transition/Setup labels on
  // recovery nodes (legacy fallback), get the Transition theme.
  if (
    safeType === "transition" ||
    (isTransitionLabel(label) &&
      (safeType === "rest" || safeType === "setrest"))
  ) {
    return { icon: iconMap.Transition, theme: themeMap.Transition };
  }

  // 1st pass (Work-only): pick a category icon from the label keyword so a
  // rower, pull-up, and ball slam don't all share the dumbbell glyph. Theme
  // stays as Work so colour semantics are preserved.
  if (safeType === "work" || safeType === "warmup" || safeType === "cooldown") {
    const category = categoriseExercise(label);
    if (category && categoryIconMap[category]) {
      const themeKey =
        safeType === "warmup"
          ? "Warmup"
          : safeType === "cooldown"
            ? "Cooldown"
            : "Work";
      return {
        icon: categoryIconMap[category],
        theme: themeMap[themeKey] || themeMap.Default,
      };
    }
  }

  // 2nd pass: try to match the explicit type (case-insensitive).
  let normalizedType = Object.keys(iconMap).find(
    (key) => key.toLowerCase() === safeType,
  );

  // 3rd pass: if type is missing or invalid, try inferring from the label.
  if (!normalizedType) {
    normalizedType = Object.keys(iconMap).find((key) =>
      safeLabel.includes(key.toLowerCase()),
    );
  }

  normalizedType = normalizedType || "Default";

  return {
    icon: iconMap[normalizedType],
    theme: themeMap[normalizedType] || themeMap.Default,
  };
};

// Precedence list for deriving a Group's theme from its work-like children.
// First match wins — a "Strength + Cardio" mixed group reads as Strength.
const GROUP_PRIORITY = ["Strength", "HIIT", "Cardio", "Mobility", "Work"];

// Derive a Group/Circuit node's theme. Label-based overrides (Warmup,
// Cooldown) win first; otherwise tally the children's effective type and
// pick the highest-priority match. Recursion is depth-limited.
export const getGroupTheme = (node, depth = 0) => {
  const labelKey = String(node?.label || "").trim().toLowerCase();
  // Substring match so "Dynamic Warm-up" and "Cooldown Sequence" both
  // resolve to their phase colour. Cooldown is checked first because
  // "cooldown" doesn't contain "warmup", but order is harmless either way.
  if (
    labelKey.includes("cooldown") ||
    labelKey.includes("cool-down") ||
    labelKey.includes("cool down")
  ) {
    return themeMap.Cooldown;
  }
  if (
    labelKey.includes("warmup") ||
    labelKey.includes("warm-up") ||
    labelKey.includes("warm up")
  ) {
    return themeMap.Warmup;
  }
  if (!Array.isArray(node?.children) || depth > 3) return themeMap.Group;

  // Reverse-lookup map from theme object → themeMap key, used to attribute
  // a recursed Group's derived theme back into the parent's tally.
  const themeToKey = new Map();
  for (const [k, v] of Object.entries(themeMap)) themeToKey.set(v, k);

  const childTypes = new Set();
  for (const c of node.children) {
    const t = String(c?.type || "").trim();
    if (t) childTypes.add(t);
    if (t === "Group" || t === "Circuit") {
      const inner = getGroupTheme(c, depth + 1);
      const key = themeToKey.get(inner);
      if (key) childTypes.add(key);
    }
  }
  for (const candidate of GROUP_PRIORITY) {
    if (childTypes.has(candidate)) return themeMap[candidate];
  }
  return themeMap.Group;
};

// Theme + icon resolver that knows about node-level context (children).
// Use this from renderers that have the full node available. Falls through
// to getVisualsForType for leaves and unknown shapes.
export const getVisualsForNode = (node) => {
  const t = String(node?.type || "").trim();
  if (t === "Group" || t === "Circuit") {
    return { icon: iconMap.Group, theme: getGroupTheme(node) };
  }
  return getVisualsForType(node?.type, node?.label);
};
